import type { Album } from './api';

/**
 * Per-account album metadata cache.
 *
 * Cover images remain in the bounded service-worker photo cache; this store
 * only remembers which authorized file is the cover so we do not relist every
 * album folder on each visit.
 */
const DATABASE_NAME = 'family-photos-private-albums-v1';
const STORE_NAME = 'albums';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_ALBUMS = 100;

interface AlbumCacheEntry {
  userId: string;
  expiresAt: number;
  albums: Album[];
  covers: Record<string, string | null>;
}

export interface CachedAlbums {
  albums: Album[];
  covers: Record<string, string | null>;
}

export async function readCachedAlbums(userId: string): Promise<CachedAlbums | null> {
  const database = await openDatabase();
  if (!database) return null;
  return new Promise((resolve) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(userId);
    request.onsuccess = () => {
      const entry = request.result as AlbumCacheEntry | undefined;
      if (!entry || entry.userId !== userId || entry.expiresAt <= Date.now()) {
        if (entry) store.delete(userId);
        resolve(null);
        return;
      }
      const albums = sanitizeAlbums(entry.albums);
      const paths = new Set(albums.map((album) => album.path));
      const covers = Object.fromEntries(Object.entries(entry.covers ?? {}).filter(
        ([path, fileId]) => paths.has(path) && (fileId === null || typeof fileId === 'string'),
      ));
      resolve({ albums, covers });
    };
    request.onerror = () => resolve(null);
    transaction.oncomplete = () => database.close();
    transaction.onerror = () => { database.close(); resolve(null); };
    transaction.onabort = () => { database.close(); resolve(null); };
  });
}

export async function writeCachedAlbumList(userId: string, albums: Album[]): Promise<void> {
  if (!userId) return;
  const safeAlbums = sanitizeAlbums(albums);
  await updateEntry(userId, (current) => {
    const paths = new Set(safeAlbums.map((album) => album.path));
    return {
      userId,
      expiresAt: Date.now() + CACHE_TTL_MS,
      albums: safeAlbums,
      covers: Object.fromEntries(Object.entries(current?.covers ?? {}).filter(([path]) => paths.has(path))),
    };
  });
}

export async function writeCachedAlbumCover(userId: string, album: Album, fileId: string | null): Promise<void> {
  if (!userId) return;
  await updateEntry(userId, (current) => {
    const albums = current?.albums?.some((item) => item.path === album.path)
      ? current.albums
      : sanitizeAlbums([...(current?.albums ?? []), album]);
    return {
      userId,
      expiresAt: Date.now() + CACHE_TTL_MS,
      albums,
      covers: { ...(current?.covers ?? {}), [album.path]: fileId },
    };
  });
}

export async function clearAlbumCache(): Promise<void> {
  const database = await openDatabase();
  if (!database) return;
  await runTransaction(database, (store) => store.clear());
}

export async function purgeAlbumCacheExcept(userId: string): Promise<void> {
  const database = await openDatabase();
  if (!database) return;
  await runTransaction(database, (store) => {
    const request = store.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;
      if ((cursor.value as Partial<AlbumCacheEntry>).userId !== userId) cursor.delete();
      cursor.continue();
    };
  });
}

function sanitizeAlbums(albums: Album[]): Album[] {
  if (!Array.isArray(albums)) return [];
  return albums.filter((album) => (
    album
    && typeof album.name === 'string'
    && typeof album.path === 'string'
    && typeof album.fileId === 'string'
    && typeof album.lastModified === 'number'
  )).map((album) => ({
    name: album.name,
    path: album.path,
    fileId: album.fileId,
    lastModified: album.lastModified,
  })).slice(0, MAX_ALBUMS);
}

async function updateEntry(
  userId: string,
  update: (current: AlbumCacheEntry | undefined) => AlbumCacheEntry,
): Promise<void> {
  const database = await openDatabase();
  if (!database) return;
  await runTransaction(database, (store) => {
    const request = store.get(userId);
    request.onsuccess = () => store.put(update(request.result as AlbumCacheEntry | undefined));
  });
}

function openDatabase(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    const request = indexedDB.open(DATABASE_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: 'userId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
}

function runTransaction(
  database: IDBDatabase,
  operation: (store: IDBObjectStore) => IDBRequest | void,
): Promise<void> {
  return new Promise((resolve) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    try {
      operation(transaction.objectStore(STORE_NAME));
    } catch {
      database.close();
      resolve();
      return;
    }
    transaction.oncomplete = () => { database.close(); resolve(); };
    transaction.onerror = () => { database.close(); resolve(); };
    transaction.onabort = () => { database.close(); resolve(); };
  });
}
